import {LeftOutlined} from '@ant-design/icons';
import {Button, Card, Divider, Input, Radio} from 'antd';
import {useState} from 'react';
import {toast} from 'react-hot-toast';
import {MdAddShoppingCart, MdDeleteOutline} from 'react-icons/md';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';

import {RqGetAllProducts} from '../../reactQuery/apis';
import {removeFromCart, updateQuantity} from '../../redux/authAction';

const CartScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector(state => state.cart);

  const {data: allProducts, isLoading, error} = RqGetAllProducts();

  const getAvailableStock = item => {
    const product = allProducts?.find(p => p.id === item.productId);
    if (!product) {return 0;}

    if (product.variants?.length > 0) {
      const variant = product.variants.find(
        v => v.color === item.color,
      );

      if (!variant) {return 0;}

      if (variant.sizes?.length > 0) {
        const selectedSize =
        typeof item.size === 'object'
          ? item.size?.size
          : item.size || '';

        const sizeObj = variant.sizes.find(
          s => (s.size || '') === (selectedSize || ''),
        );

        if (!sizeObj) {
          return 0; 
        }

        return sizeObj.quantity;
      }

      return variant.quantity || 0;
    }

    return product.quantity || 0;
  };

  const toDropboxRawUrl = (url = '') =>
    url.replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');

  const handleIncreaseQuantity = item => {
    const maxQty = getAvailableStock(item);

    if (item.quantity >= maxQty) {
      toast.error('No more stock available for this product');
      return;
    }

    dispatch(
      updateQuantity({
        productId: item.productId,
        size: item.size,
        quantity: item.quantity + 1,
      }),
    );
  };

  const handleDecreaseQuantity = item => {
    if (item.quantity > 1) {
      dispatch(
        updateQuantity({
          productId: item.productId,
          size: item.size,
          quantity: item.quantity - 1,
        }),
      );
    }
  };
  const [value, setValue] = useState(1);
  const onChange = e => {
    setValue(e.target.value);
  };

  const getDiscountedPrice = product => {
    const basePrice = product.price || 0;
    const discountPercent = product.discount || 0;

    return discountPercent > 0
      ? basePrice - (basePrice * discountPercent) / 100
      : basePrice;
  };

  const getTotal = () =>
    cart.items.reduce((total, item) => {
      const product = allProducts?.find(p => p.id === item.productId);
      if (!product) {return total;}

      return total + getDiscountedPrice(product) * item.quantity;
    }, 0);

  const getTotalProducts = () => cart.items.length;
  const getTotalQuantity = () => cart.items.reduce((total, item) => total + item.quantity, 0);

  const isOutOfStock = item => item.quantity > getAvailableStock(item);
  const remainingStock = (product, item) => {
    if (!product) {return 0;}

    if (product.sizes && item.size) {
      const sizeObj = product.sizes.find(
        s => s.size === (typeof item.size === 'object' ? item.size.size : item.size),
      );

      if (!sizeObj) {return 0;}

      return sizeObj.stock - item.quantity;
    }

    return product.stock - item.quantity;
  };

  const isCheckoutDisabled = () => cart.items.some(item => isOutOfStock(item));

  if (isLoading) {return <div>Loading cart...</div>;}
  if (error) {return <div>Error fetching products</div>;}

  return (
    <div className={'w-full px-4 lg:px-20 py-4 flex lg:flex-row flex-col justify-center items-center gap-4 relative'}>
      <div className={'max-w-6xl w-full'}>
        {cart.items.length === 0 ? (
          <div className={'col-span-full flex flex-col items-center justify-center h-80'}>
            <h1 className={'font-semibold text-primary-900 lg:text-xl font-serif-custom'}>
              Your cart is empty...!
            </h1>
            <Link to={'/product'}>
              <Button className={'mt-5 w-56 h-8 rounded-lg border-none bg-primary-900 text-white flex items-center justify-center'}>
                <MdAddShoppingCart className={'mr-2'} /> Continue Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className={'grid grid-cols-1 lg:grid-cols-12 lg:gap-5 w-full mx-auto p-4 lg:p-0'}>
            <div className={'lg:col-span-12 mt-5'}>
              <Link to={'/product'}>
                <Button color={'default'} variant={'link'} className={'p-0 !text-primary-900 font-semibold mb-3 lg:text-lg'}>
                  <LeftOutlined /> Continue Shopping
                </Button>
              </Link>
            </div>

            {/* Cart Items */}
            <div className={'lg:col-span-7'}>
              {cart.items.map((item, index) => {
                const product = allProducts?.find(p => p.id === item.productId);

                // If product no longer exists (deleted by admin)
                if (!product) {
                  return null;
                }
                const getVariantImage = (product, item) => {
                  if (!product?.variants?.length) {return '';}

                  // Find selected color variant
                  const selectedVariant = product.variants.find(
                    v => v.color === item.color,
                  );

                  // If variant found and has images
                  if (selectedVariant?.images?.length) {
                    return selectedVariant.images[0]; // index 0 image
                  }

                  // Fallback: first available variant image
                  for (const variant of product.variants) {
                    if (variant.images?.length) {
                      return variant.images[0];
                    }
                  }

                  return '';
                };

                const latestName = product.productName;
                const basePrice = product.price || 0;
                const discountPercent = product.discount || 0;

                const discountedPrice =
  discountPercent > 0
    ? basePrice - (basePrice * discountPercent) / 100
    : basePrice;

                const latestImage = getVariantImage(product, item);

                const normalizeImageUrl = (url = '') => {
                  if (!url) {return '';}

                  if (url.includes('dropbox.com')) {
                    return url
                      .replace('www.dropbox.com', 'dl.dropboxusercontent.com')
                      .replace('?dl=0', '');
                  }

                  return url; // AWS S3 or any other CDN
                };

                const imageSrc = normalizeImageUrl(latestImage);
                console.log('PRODUCT IMAGE DATA:', product.imageUrl);

                return (
                  <Card
                    key={`${item.productId}-${item.size}-${index}`}
                    className={'mb-2 w-full shadow-md max-h-[250px] my-auto'}
                  >
                    <div className={'flex md:flex-row flex-col justify-between'}>
                      <div className={'flex flex-row'}>
                        <div className={'w-20 h-20 sm:w-24 sm:h-28 md:w-28 md:h-28 lg:w-36 lg:h-36'}>
                          <img
                            alt={latestName}
                            src={imageSrc}
                            className={'w-full h-full object-cover rounded-lg'}
                          />
                        </div>

                        <div className={'ml-4 lg:font-medium'}>
                          <h1 className={'font-semibold md:text-lg text-base text-primary-900'}>
                            {latestName}
                          </h1>

                          <h1 className={'font-medium flex md:text-base'}>
                            {/* {discountPercent > 0 && (
                              <span className={'text-gray-400 line-through text-sm'}>
    ₹ {basePrice}
                              </span>
                            )} */}
  Price per Unit : ₹ {Math.round(discountedPrice)}
                          </h1>

                          {item.size && (
                            <h1>
                  Size: {typeof item.size === 'object'
                                ? item.size.size
                                : item.size}
                            </h1>
                          )}

                          <h1 className={'font-medium md:text-base'}>
                Quantity : {item.quantity}
                          </h1>

                          {remainingStock(product, item) > 3 && (
                            <p className={'text-primary-900 text-sm'}>In stock</p>
                          )}
                          {remainingStock(product, item) === 2 && (
                            <p className={'text-[#FF0000] text-sm'}>
                  Only 2 products remaining!
                            </p>
                          )}
                          {remainingStock(product, item) === 1 && (
                            <p className={'text-[#FF0000] text-sm'}>
                  Only 1 product remaining!
                            </p>
                          )}
                          {remainingStock(product, item) <= 0 && (
                            <p className={'text-[#FF0000] text-sm'}>
                  You reached max stock for this product
                            </p>
                          )}

                          {/* Quantity Controls */}
                          <div className={'flex flex-row items-center lg:space-x-3 space-x-2 lg:mt-4 mt-2'}>
                            <Button
                              onClick={() => handleDecreaseQuantity(item)}
                              disabled={item.quantity === 1}
                              className={'w-6 h-6 rounded-lg !bg-primary-100 border-none'}
                            >
                  -
                            </Button>

                            <span className={'text-lg font-semibold'}>
                              {item.quantity}
                            </span>

                            <Button
                              onClick={() => handleIncreaseQuantity(item)}
                              disabled={remainingStock(product, item) <= 0}
                              className={'w-6 h-6 rounded-lg !bg-primary-100 border-none'}
                            >
                  +
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className={'flex md:flex-col flex-row-reverse md:gap-2 gap-32 items-end md:font-medium'}>
                        <MdDeleteOutline
                          onClick={() =>
                            dispatch(
                              removeFromCart({
                                productId: item.productId,
                                size: item.size,
                              }),
                            )
                          }
                          className={'text-lg text-red-700 cursor-pointer lg:mt-2'}
                        />

                        <h1 className={'font-semibold text-lg text-primary-900 md:pt-14 pt-2'}>
              ₹ {item.quantity *  Math.round(discountedPrice)}
                        </h1>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className={'lg:col-span-5 lg:ml-4 lg:sticky top-0 self-start'}>
              <div className={'bg-primary-50 rounded-lg p-3 max-w-[800px] w-full'}>
                <h1 className={'font-semibold text-primary-900 text-lg'}>Order Summary:</h1>
                <Divider className={'my-1'} />
                <div className={'flex justify-between md:text-base font-semibold mb-3'}>
                  <p>Subtotal:</p>
                  <p>&#8377;{Math.round(getTotal())}</p>
                </div>
                <div className={'flex justify-between md:text-base font-semibold mb-3'}>
                  <p>Total Products:</p>
                  <p>{getTotalProducts()}</p>
                </div>
                <div className={'flex justify-between md:text-base font-semibold mb-3'}>
                  <p>Total Quantity:</p>
                  <p>{getTotalQuantity()}</p>
                </div>
              </div>
              <Button
                disabled={isCheckoutDisabled()}
                onClick={() => !isCheckoutDisabled() && navigate('/checkout')}
                className={'mt-5 w-full h-8 rounded-lg bg-primary-800 text-white'}
              >
                Continue to Checkout
              </Button>
              {isCheckoutDisabled() && (
                <p className={'text-[#FF0000] text-sm mt-2'}>
                  Some products in your cart are out of stock. Please remove them.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartScreen;
